import {
  Avatar,
  Box,
  Divider,
  Link,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import sortBy from 'lodash/sortBy'
import React, { useMemo } from 'react'
import { Fade, IconButton, Icons, SerialNumber, SkeletonText } from 'utoniq-ui'

import {
  ItemInformationQuery,
  Maybe,
  SerialItemOwnersFragment,
  UserInfoFragment,
  UserItemSerial,
} from '@/clients/utoniq-core/schema'
import { Head, Header } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  ItemDescription,
  ItemDescriptionSkeleton,
} from '@/components/organisms'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { styles } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { decodeSkuId } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  userId?: string
  hasNextPage: boolean
  number: number
  serials: Maybe<UserItemSerial[]>
  item: ItemInformationQuery['item']
  user: Maybe<UserInfoFragment>
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ItemInformationTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('itemInformation')
  const { routerNames } = useRouter()
  const { onClick } = useBrowserLink(getHelpUrl('item'))

  return (
    <Box
      sx={{
        pt: styles.safeAreaTop,
        pb: styles.safeAreaBottom,
        overflowY: 'scroll',
      }}
    >
      <Head
        title={[
          !state.userId
            ? routerNames.OwnedItemInformation
            : routerNames.UserItemInformation,
          state.item?.title,
          state.user?.displayName,
        ]}
      />

      <Header
        {...{
          title: t('title'),
          back: true,
          right: (
            <IconButton.Help
              edge="end"
              color="primary.main"
              onClick={onClick}
            />
          ),
        }}
      />

      <Box sx={{ width: '100%', maxWidth: 600, margin: '0 auto', pt: '56px' }}>
        <Box sx={{ pt: 3, px: 5 }}>
          {state.loading || !state.item ? (
            <ItemDescriptionSkeleton />
          ) : (
            <Fade>
              <ItemDescription
                item={state.item}
                number={state.number}
                user={state.user}
              />
            </Fade>
          )}
        </Box>
        {!!state.item?.metadata?.ticket?.note && (
          <Stack spacing={1} sx={{ my: 4, px: 5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Icons.Info fontSize="small" sx={{ color: 'grey.600' }} />
              <Typography variant="h6" color="text.secondary">
                {t('ticketNote')}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {state.item?.metadata?.ticket?.note}
            </Typography>
          </Stack>
        )}
        {/**
         * @description
         * Open when implemented data in the future
         * https://app.clickup.com/t/908127/UT-13603
         */}
        {/*<Stack spacing={1} sx={{ px: 5 }}>*/}
        {/*  <Typography variant="body2" color="text.secondary">*/}
        {/*    {t('assignedNumber', {'assignedNumber': 'A1000'})}*/}
        {/*  </Typography>*/}
        {/*</Stack>*/}
        {!state.loading && !!state.item && (
          <SerialOwnersCell
            serials={state.serials ?? null}
            user={state.user ?? null}
            itemSerials={state.item?.serials ?? null}
            onFetchMore={onFetchMore}
            hasNextPage={state.hasNextPage}
            loading={state.loading || state.fetching}
            hideSalesNumber={!!state.item?.hideSalesNumber}
          />
        )}
      </Box>
    </Box>
  )
}

type SerialOwnersCellProps = {
  showSku?: boolean
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  user: Maybe<UserInfoFragment>
  serials: Maybe<UserItemSerial[]>
  itemSerials: Maybe<SerialItemOwnersFragment>
  onFetchMore: Action['onFetchMore']
  hideSalesNumber: boolean
}
export const SerialOwnersCell: Cell<SerialOwnersCellProps> = ({
  showSku,
  loading,
  hasNextPage,
  user,
  serials,
  itemSerials,
  onFetchMore,
  hideSalesNumber,
}) => {
  const { t } = useTranslation('itemInformation')
  const { routerPages, generatePath, router } = useRouter<{ skuId?: string }>()

  const serialSorted = useMemo<UserItemSerial[]>(() => {
    if (!serials || !serials.length) {
      return []
    }
    return sortBy(serials, ({ formattedSerialNumber }) =>
      parseInt(formattedSerialNumber?.replace('#', '') ?? '0')
    )
  }, [serials])

  const concatSerial = useMemo<
    Array<UserItemSerial | SerialItemOwnersFragment['edges'][number]>
  >(() => {
    // 所持シリアル以外のシリアルについて、SKUがある場合skuIdが一致するの以外を除外する
    const edges = (itemSerials?.edges || []).filter(({ node }) => {
      if (!router.query.skuId || !node || !node.sku) return true

      return node.sku.id === decodeSkuId(router.query.skuId)
    })

    return [...serialSorted, ...edges]
  }, [serialSorted, itemSerials?.edges])

  if (
    !!hideSalesNumber &&
    (!serials?.length ||
      // O-MENZのアプリは、シリアル非表示設定であっても、自分のアイテムのシリアル番号は分かるにする UT-11803
      config.applicationKey !== 'omenz')
  )
    return null

  return (
    <CellFrame sx={{ pt: 7 }}>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ py: 2, px: 5 }}
      >
        {t('ownerList')}
      </Typography>
      <Box sx={{ pb: 2 }}>
        <TableContainer>
          <Table aria-label="serial-number-table" sx={{ width: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 5 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    sx={{ display: 'block' }}
                  >
                    {t('serialNumber')}
                  </Typography>
                </TableCell>
                {showSku && (
                  <TableCell
                    align="left"
                    sx={{ border: 'none', minWidth: 100, py: 2, px: 3 }}
                  >
                    <Typography variant="overline" color="text.hint">
                      {t('sku')}
                    </Typography>
                  </TableCell>
                )}
                <TableCell
                  align="left"
                  sx={{ border: 'none', minWidth: 100, py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('acquisitionDate')}
                  </Typography>
                </TableCell>
                <TableCell
                  width="100%"
                  align="left"
                  sx={{ border: 'none', py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('owner')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableScrollFetcher
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              component={TableRow}
              colSpan={3}
              nodes={concatSerial?.map(serial => {
                if (
                  !isUserItemSerial(serial) &&
                  serialSorted.find(
                    o =>
                      o.formattedSerialNumber ===
                      serial.node.formattedSerialNumber
                  )
                ) {
                  return <TableCell padding="none" sx={{ border: 'none' }} />
                }
                const serialUser = isUserItemSerial(serial)
                  ? user
                  : serial.node.user
                const isNft = isUserItemSerial(serial)
                  ? !!serial.isNft
                  : !!serial.node.isNft
                return (
                  <>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        pl: 5,
                        pr: 0,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pr: 2 }}
                      >
                        <SerialNumber
                          label={
                            isUserItemSerial(serial)
                              ? serial.formattedSerialNumber
                              : serial.node.formattedSerialNumber
                          }
                        />
                        {isNft && <Icons.Nft fontSize="small" filledColor />}
                      </Stack>
                    </TableCell>
                    {showSku && (
                      <TableCell
                        padding="none"
                        sx={{
                          py: 2,
                          px: 0,
                          border: 'none',
                          bgcolor: isUserItemSerial(serial)
                            ? 'grey.50'
                            : 'unset',
                        }}
                      >
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ height: 20 }}
                          />
                          <Typography variant="subtitle2">
                            {isUserItemSerial(serial)
                              ? serial.serial?.sku?.unitName
                              : serial.node.sku?.unitName}
                          </Typography>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        px: 0,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 20 }}
                        />
                        {serialUser && (
                          <Typography variant="overline">
                            {isUserItemSerial(serial)
                              ? serial.formattedObtainedDate
                              : serial.node?.formattedObtainedDate}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        pl: 0,
                        pr: 5,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Stack>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ height: 20 }}
                          />
                        </Stack>
                        {serialUser && (
                          <Stack
                            component={Link}
                            underline="none"
                            href={generatePath(routerPages.UserProfile, {
                              userId: serialUser.id,
                            })}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Avatar
                              src={serialUser.avatar?.smallUri ?? ''}
                              sx={{ width: 28, height: 28 }}
                            />
                            <Typography variant="overline">
                              {isUserItemSerial(serial)
                                ? user?.displayName
                                : serial.node.user?.displayName}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                  </>
                )
              })}
              loadingComponent={
                <TableRow>
                  <TableCell
                    padding="none"
                    colSpan={showSku ? 4 : 3}
                    sx={{ border: 'none' }}
                  >
                    <Stack flex={1} spacing={3} sx={{ px: 5 }}>
                      <SkeletonText
                        variant="large"
                        numberOfLine={8}
                        lastWidth="100%"
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              }
            />
          </Table>
        </TableContainer>
      </Box>
    </CellFrame>
  )
}

function isUserItemSerial(
  serial: UserItemSerial | SerialItemOwnersFragment['edges'][number]
): serial is UserItemSerial {
  return 'serialId' in serial
}
